import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { ScalesIcon as Scales, ClockCountdownIcon as ClockCountdown, CheckCircleIcon as CheckCircle, ArrowRightIcon as ArrowRight, HandshakeIcon as Handshake } from "@phosphor-icons/react";

/**
 * Path-1 venue posture (Iter 204): in-app UI for the consent-propose /
 * consent-accept flow on disputed escrow + transport rows.
 *
 * Used on:
 *   - MyEscrow.jsx EscrowRow (when status === "disputed")
 *   - MyTransport.jsx order detail (when dispute.opened_at && !dispute.resolved_at)
 *
 * The component is intentionally surface-agnostic — it doesn't know
 * whether it's wrapping an escrow row or a transport order. You give
 * it:
 *
 *   <ConsentResolutionPanel
 *     proposeUrl="/escrow/abc/consent/propose"
 *     acceptUrl="/escrow/abc/consent/accept"
 *     proposals={row.consent_proposals || []}
 *     currentUserId={user.id}
 *     myRoleLabel="buyer" | "seller" | "transporter"
 *     counterpartyLabel="breeder" | "buyer" | "transporter"
 *     disputeFiledAt={row.disputed_at}
 *     onResolved={reload}
 *   />
 *
 * `proposals` is a list of `{id, proposed_by_user_id, proposed_by_role,
 * outcome, buyer_refund_pct, note, created_at, status}` rows that the
 * backend stores on the parent doc.
 *
 * Both the propose and accept paths POST to the URLs you supply, so
 * this same component works for `/escrow/{id}/...` and
 * `/transport/orders/{id}/...` without any branching.
 */
const OUTCOMES = [
  {
    id: "refund",
    label: "Full refund to buyer",
    blurb: "Money goes back to the buyer. Dog/order stays with you in the seller-/transporter-side state. Equivalent to what the 30-day timer would do automatically.",
  },
  {
    id: "release",
    label: "Full release to seller",
    blurb: "Funds released in full. This ends the dispute with no refund.",
  },
  {
    id: "split",
    label: "Partial refund (split)",
    blurb: "Refund a percentage of the total to the buyer; release the remainder to the seller. Pick the % below.",
  },
];

function daysUntilAutoResolve(disputeFiledAt) {
  if (!disputeFiledAt) return null;
  const ms = new Date(disputeFiledAt).getTime() + 30 * 24 * 60 * 60 * 1000 - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export default function ConsentResolutionPanel({
  proposeUrl,
  acceptUrl,
  proposals = [],
  currentUserId,
  myRoleLabel = "you",
  counterpartyLabel = "the other party",
  disputeFiledAt,
  onResolved,
  testIdPrefix = "consent",
}) {
  const [outcome, setOutcome] = useState("refund");
  const [pct, setPct] = useState(50);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const pending = useMemo(
    () => (proposals || []).filter((p) => p?.status === "pending"),
    [proposals],
  );
  // A proposal I can accept = pending AND not authored by me.
  const acceptable = pending.filter((p) => p.proposed_by_user_id !== currentUserId);
  // A pending proposal I authored (waiting on the other side).
  const mineWaiting = pending.filter((p) => p.proposed_by_user_id === currentUserId);

  const daysLeft = daysUntilAutoResolve(disputeFiledAt);

  const propose = async () => {
    setBusy(true);
    try {
      await api.post(proposeUrl, {
        outcome,
        buyer_refund_pct: outcome === "split" ? Number(pct) : null,
        note: note.trim() || null,
      });
      toast.success(`Sent. ${counterpartyLabel} has to accept for the resolution to execute.`);
      setNote("");
      onResolved && onResolved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const accept = async (proposalId) => {
    setBusy(true);
    try {
      await api.post(acceptUrl, { proposal_id: proposalId });
      toast.success("Resolved. The platform has executed the agreed outcome.");
      onResolved && onResolved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-4 pt-4 border-t border-[#E5E5E5] space-y-4"
      data-testid={`${testIdPrefix}-panel`}
    >
      {/* 30-day countdown — the most important UI element. The clock is
          the published default. Making it visible (and tangible)
          encourages mutual agreement before it fires. */}
      {daysLeft !== null && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
          data-testid={`${testIdPrefix}-countdown`}
        >
          <ClockCountdown size={20} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-[13px] text-amber-900 leading-relaxed">
            <strong>
              {daysLeft === 0
                ? "Auto-refund to buyer is due now."
                : `Auto-refund to buyer in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
              .
            </strong>{" "}
            If you and {counterpartyLabel} don&apos;t agree on another
            outcome by then, the platform will mechanically refund the
            buyer in full. Armstrong K9 Registry does not decide
            disputes; the timer or your mutual agreement does.{" "}
            <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline font-semibold">
              See policy
            </a>
            .
          </div>
        </div>
      )}

      {/* PROPOSAL TO ACCEPT — front-of-mind item when the other party
          has already proposed. We render this above the "propose your
          own" form so the user doesn't propose a counter without
          seeing what's on the table. */}
      {acceptable.map((p) => (
        <div
          key={p.id}
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
          data-testid={`${testIdPrefix}-acceptable-${p.id}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Handshake size={18} weight="duotone" className="text-emerald-700" />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-800">
              {p.proposed_by_role.toLowerCase()} proposed
            </span>
          </div>
          <p className="font-spectral text-lg text-ink mb-1">
            {p.outcome === "refund" && "Full refund to buyer"}
            {p.outcome === "release" && "Full release to seller"}
            {p.outcome === "split" && `Split — ${p.buyer_refund_pct}% to buyer, ${100 - Number(p.buyer_refund_pct)}% to seller`}
          </p>
          {p.note && <p className="text-[13px] text-[#3F3F3F] mb-3 leading-relaxed">&ldquo;{p.note}&rdquo;</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => accept(p.id)}
              disabled={busy}
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid={`${testIdPrefix}-accept-${p.id}`}
            >
              <CheckCircle size={14} weight="fill" className="mr-1.5" />
              Agree &amp; execute
            </Button>
            <span className="text-[12px] text-[#6B6B6B]">
              Accepting runs the agreed Stripe operation immediately and ends the dispute.
            </span>
          </div>
        </div>
      ))}

      {/* OWN PENDING PROPOSALS — read-only "you proposed this; awaiting them" */}
      {mineWaiting.map((p) => (
        <div
          key={p.id}
          className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-[13px]"
          data-testid={`${testIdPrefix}-mine-${p.id}`}
        >
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#6B6B6B]">
            You proposed · waiting on {counterpartyLabel}
          </span>
          <p className="text-ink mt-1">
            {p.outcome === "refund" && "Full refund to buyer"}
            {p.outcome === "release" && "Full release to seller"}
            {p.outcome === "split" && `${p.buyer_refund_pct}% to buyer / ${100 - Number(p.buyer_refund_pct)}% to seller`}
          </p>
          {p.note && <p className="text-[12px] text-[#6B6B6B] mt-1 italic">&ldquo;{p.note}&rdquo;</p>}
        </div>
      ))}

      {/* PROPOSE YOUR OWN */}
      <div data-testid={`${testIdPrefix}-propose-form`}>
        <div className="flex items-center gap-2 mb-3">
          <Scales size={18} weight="duotone" className="text-ink" />
          <h4 className="font-spectral text-lg text-ink">
            {acceptable.length || mineWaiting.length ? "Counter-propose" : "Propose a resolution"}
          </h4>
        </div>
        <p className="text-[12px] text-[#6B6B6B] mb-3 leading-relaxed">
          Both parties have to agree for any outcome to execute.{" "}
          {myRoleLabel === "buyer" ? counterpartyLabel : "The buyer"} will see
          your proposal in their inbox and can accept it, counter, or
          let the 30-day timer decide.
        </p>

        <RadioGroup
          value={outcome}
          onValueChange={setOutcome}
          className="space-y-2"
          data-testid={`${testIdPrefix}-outcome-radio`}
        >
          {OUTCOMES.map((o) => (
            <label
              key={o.id}
              htmlFor={`${testIdPrefix}-outcome-${o.id}`}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                outcome === o.id ? "border-ink bg-cream/40" : "border-[#E5E5E5] hover:border-[#D5D5D5]"
              }`}
            >
              <RadioGroupItem
                value={o.id}
                id={`${testIdPrefix}-outcome-${o.id}`}
                className="mt-1"
                data-testid={`${testIdPrefix}-outcome-${o.id}`}
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink text-[14px]">{o.label}</p>
                <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-relaxed">{o.blurb}</p>
              </div>
            </label>
          ))}
        </RadioGroup>

        {outcome === "split" && (
          <div className="mt-3" data-testid={`${testIdPrefix}-split-pct-wrap`}>
            <Label htmlFor={`${testIdPrefix}-split-pct`} className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#6B6B6B]">
              % refunded to buyer (1–99)
            </Label>
            <Input
              id={`${testIdPrefix}-split-pct`}
              type="number"
              min={1}
              max={99}
              value={pct}
              onChange={(e) => setPct(Math.max(1, Math.min(99, Number(e.target.value) || 0)))}
              className="mt-1 max-w-[120px]"
              data-testid={`${testIdPrefix}-split-pct`}
            />
            <p className="text-[11.5px] text-[#6B6B6B] mt-1">
              Buyer receives {pct}%, seller receives {100 - pct}%.
            </p>
          </div>
        )}

        <div className="mt-3">
          <Label htmlFor={`${testIdPrefix}-note`} className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#6B6B6B]">
            Note to {counterpartyLabel} (optional, 2000 chars)
          </Label>
          <Textarea
            id={`${testIdPrefix}-note`}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            placeholder="A short explanation of why this outcome feels fair to you."
            rows={3}
            className="mt-1"
            data-testid={`${testIdPrefix}-note`}
          />
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            onClick={propose}
            disabled={busy}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid={`${testIdPrefix}-propose-btn`}
          >
            Send proposal
            <ArrowRight size={14} weight="bold" className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
